/**
 * MetricsSummaryWidget — Generic metrics display (DRR-319/320)
 *
 * YAML-first: No new resolvers. Uses existing queries + constellation config.
 *
 * Two modes:
 * 1. Data-driven: Parent passes `metrics` array directly (preferred)
 * 2. Self-fetch: Uses existing fhirObservationsBySubject query
 *
 * Threshold evaluation comes from ruleset YAML, applied by:
 * - Backend (via Zen Engine) returning thresholdStatus, OR
 * - Frontend evaluating simple threshold rules from config
 *
 * This widget does NOT know about healthcare, finance, or any domain.
 */

import React, { useMemo } from 'react';
import { Group, Text, Badge, Stack, Paper, Skeleton, Tooltip, ThemeIcon } from '@mantine/core';
import {
  IconTrendingUp,
  IconTrendingDown,
  IconMinus,
  IconAlertTriangle,
  IconCheck,
} from '@tabler/icons-react';
import { registerWidget, type WidgetProps } from './DashboardWidgetRegistry';

export interface MetricData {
  key: string;
  label: string;
  value: string | number;
  unit?: string;
  timestamp?: string;
  trend?: 'up' | 'down' | 'stable';
  trendValue?: number;
  thresholdStatus?: 'normal' | 'warning' | 'critical';
  thresholdColor?: string;
}

export interface MetricThreshold {
  key: string;
  warning?: { min?: number; max?: number };
  critical?: { min?: number; max?: number };
}

interface MetricsSummaryWidgetProps extends WidgetProps {
  metrics?: MetricData[];
  thresholds?: MetricThreshold[];
}

function evaluateThreshold(value: number, threshold?: MetricThreshold): 'normal' | 'warning' | 'critical' {
  if (!threshold) return 'normal';

  if (threshold.critical) {
    if (threshold.critical.max !== undefined && value > threshold.critical.max) return 'critical';
    if (threshold.critical.min !== undefined && value < threshold.critical.min) return 'critical';
  }
  if (threshold.warning) {
    if (threshold.warning.max !== undefined && value > threshold.warning.max) return 'warning';
    if (threshold.warning.min !== undefined && value < threshold.warning.min) return 'warning';
  }
  return 'normal';
}

function TrendIndicator({ trend, value }: { trend?: string; value?: number }) {
  if (!trend && value === undefined) return null;

  if (value !== undefined && value !== null) {
    const isUp = value > 0;
    const isDown = value < 0;
    const Icon = isUp ? IconTrendingUp : isDown ? IconTrendingDown : IconMinus;
    return (
      <Tooltip label={`${isUp ? '+' : ''}${value} change`}>
        <Icon size={12} color={isUp ? 'orange' : isDown ? 'blue' : 'gray'} />
      </Tooltip>
    );
  }

  if (trend === 'up') return <IconTrendingUp size={12} color="orange" />;
  if (trend === 'down') return <IconTrendingDown size={12} color="blue" />;
  return <IconMinus size={12} color="gray" />;
}

function getStatusIcon(status?: string) {
  switch (status) {
    case 'critical':
    case 'warning':
      return <IconAlertTriangle size={14} />;
    default:
      return <IconCheck size={14} />;
  }
}

function getStatusColor(status?: string, thresholdColor?: string): string {
  if (thresholdColor) return thresholdColor;
  switch (status) {
    case 'critical':
      return 'red';
    case 'warning':
      return 'orange';
    default:
      return 'green';
  }
}

export function MetricsSummaryWidget({
  tenantNum,
  subjectActorId,
  config,
  metrics: providedMetrics,
  thresholds: providedThresholds,
}: MetricsSummaryWidgetProps) {
  const compact = config?.compact as boolean ?? false;
  const configThresholds = config?.thresholds as MetricThreshold[] | undefined;

  const thresholds = providedThresholds ?? configThresholds ?? [];

  const metrics = useMemo(() => {
    if (!providedMetrics) return [];

    return providedMetrics.map(metric => {
      if (metric.thresholdStatus) return metric;

      const numValue = typeof metric.value === 'number' ? metric.value : parseFloat(String(metric.value));
      if (isNaN(numValue)) return metric;

      const threshold = thresholds.find(t => t.key === metric.key);
      const status = evaluateThreshold(numValue, threshold);

      return { ...metric, thresholdStatus: status };
    });
  }, [providedMetrics, thresholds]);

  if (!providedMetrics) {
    return (
      <Text size="xs" c="dimmed">
        Metrics data not provided. Pass metrics prop from parent.
      </Text>
    );
  }

  if (metrics.length === 0) {
    return <Text size="xs" c="dimmed">No metrics available</Text>;
  }

  if (compact) {
    return (
      <Group gap="xs">
        {metrics.map((metric) => (
          <Badge
            key={metric.key}
            size="sm"
            color={getStatusColor(metric.thresholdStatus, metric.thresholdColor)}
            variant="light"
          >
            {metric.label}: {metric.value}{metric.unit ? ` ${metric.unit}` : ''}
          </Badge>
        ))}
      </Group>
    );
  }

  return (
    <Group gap="md">
      {metrics.map((metric) => {
        const color = getStatusColor(metric.thresholdStatus, metric.thresholdColor);
        return (
          <Paper key={metric.key} p="xs" withBorder radius="sm">
            <Stack gap={2} align="center">
              <Group gap={4}>
                <ThemeIcon size="xs" color={color} variant="light">
                  {getStatusIcon(metric.thresholdStatus)}
                </ThemeIcon>
                <Text size="xs" c="dimmed">{metric.label}</Text>
              </Group>
              <Group gap={4}>
                <Text fw={600} size="sm">{metric.value}</Text>
                {metric.unit && <Text size="xs" c="dimmed">{metric.unit}</Text>}
                <TrendIndicator trend={metric.trend} value={metric.trendValue} />
              </Group>
            </Stack>
          </Paper>
        );
      })}
    </Group>
  );
}

registerWidget('metrics-summary', MetricsSummaryWidget, 'core', 'Generic metrics display - pass metrics data from parent');
